import { Capacitor } from '@capacitor/core';
import { Posthog } from '@capawesome/capacitor-posthog';

// Access environment variables
// Vue CLI replaces process.env.VUE_APP_* at build time
const POSTHOG_API_KEY = process.env.VUE_APP_POSTHOG_TOKEN || null;
const POSTHOG_HOST = 'https://muddy-lab-a051.exadeci.workers.dev/hog';

let isInitialized = false;
let initializationAttempts = 0;
const MAX_INIT_ATTEMPTS = 3;
let eventQueue = [];
const MAX_QUEUE_SIZE = 100;

class AnalyticsService {
    constructor() {
        this.initialized = false;
        this.deviceInfo = null;
    }

    // Setup global error handlers
    setupGlobalErrorHandlers() {
        // Handle uncaught errors
        window.onerror = (message, source, lineno, colno, error) => {
            this.track('uncaught_error', {
                message,
                source,
                lineno,
                colno,
                error: error?.message,
                stack: error?.stack,
                platform: Capacitor.getPlatform(),
                url: window.location.href,
                userAgent: navigator.userAgent
            });
        };

        // Handle unhandled promise rejections
        window.onunhandledrejection = (event) => {
            this.track('unhandled_rejection', {
                reason: event.reason?.message || event.reason,
                stack: event.reason?.stack,
                platform: Capacitor.getPlatform(),
                url: window.location.href,
                userAgent: navigator.userAgent
            });
        };
    }

    // Flush queued events
    async flushEventQueue() {
        if (eventQueue.length === 0) return;
        
        console.log(`[PostHog] Flushing ${eventQueue.length} queued events...`);
        const events = [...eventQueue];
        eventQueue = [];
        
        for (const event of events) {
            try {
                await Posthog.capture(event);
            } catch (error) {
                console.debug('[PostHog] Failed to flush queued event:', error);
                // Put the event back in the queue if it's not full
                if (eventQueue.length < MAX_QUEUE_SIZE) {
                    eventQueue.push(event);
                }
            }
        }
    }

    // Queue an event for later
    queueEvent(event) {
        if (eventQueue.length >= MAX_QUEUE_SIZE) {
            console.warn('[PostHog] Event queue full, dropping oldest event');
            eventQueue.shift();
        }
        eventQueue.push(event);
    }

    // Initialize PostHog with retry logic
    async initializePostHog() {
        if (!POSTHOG_API_KEY) {
            console.debug('[PostHog] Skipping initialization - no API key');
            return false;
        }

        try {
            console.log('[PostHog] Attempting to initialize...');
            
            // First try to flush any queued events from previous session
            await this.flushEventQueue();
            
            await Posthog.setup({
                apiKey: POSTHOG_API_KEY,
                host: POSTHOG_HOST,
            });
            
            // Test the connection with a simple event
            try {
                await Posthog.capture({
                    event: 'posthog_connection_test',
                    properties: {
                        platform: Capacitor.getPlatform(),
                        attempt: initializationAttempts + 1,
                        timestamp: new Date().toISOString(),
                        app_version: '1.0.0'
                    }
                });
            } catch (error) {
                console.warn('[PostHog] Test event failed, but continuing initialization:', error);
            }
            
            console.log('[PostHog] Successfully initialized');
            return true;
        } catch (error) {
            console.error('[PostHog] Initialization failed:', error);
            return false;
        }
    }

    async init() {
        if (this.initialized) return;

        while (initializationAttempts < MAX_INIT_ATTEMPTS && !this.initialized) {
            initializationAttempts++;
            console.log(`[PostHog] Initialization attempt ${initializationAttempts}/${MAX_INIT_ATTEMPTS}`);
            
            const success = await this.initializePostHog();
            if (success) {
                this.initialized = true;
                isInitialized = true;
                this.setupGlobalErrorHandlers();
                
                // Try to flush any queued events after successful initialization
                await this.flushEventQueue();
                
                console.log('[PostHog] Analytics initialized successfully');
                return;
            }
            
            if (initializationAttempts < MAX_INIT_ATTEMPTS) {
                const delay = Math.min(1000 * Math.pow(2, initializationAttempts - 1), 10000);
                console.log(`[PostHog] Retrying in ${delay}ms...`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }

        if (!this.initialized) {
            console.warn('[PostHog] Failed to initialize after', MAX_INIT_ATTEMPTS, 'attempts');
        }
    }

    // Identify a user
    async identify(userId, userProperties = {}) {
        if (!this.initialized) {
            console.debug('[PostHog] Cannot identify user: analytics not initialized');
            return;
        }

        try {
            console.log('[PostHog] Identifying user:', userId);
            await Posthog.identify({
                distinctId: userId,
                userProperties: {
                    ...userProperties,
                    platform: Capacitor.getPlatform(),
                    last_seen: new Date().toISOString()
                }
            });
        } catch (error) {
            console.debug('[PostHog] Failed to identify user:', error);
        }
    }

    // Track an event
    async track(eventName, properties = {}) {
        const event = {
            event: eventName,
            properties: {
                ...properties,
                platform: Capacitor.getPlatform(),
                timestamp: new Date().toISOString(),
                app_version: '1.0.0'
            }
        };

        if (!this.initialized) {
            console.debug('[PostHog] Analytics not initialized, queueing event:', eventName);
            this.queueEvent(event);
            return;
        }

        try {
            console.log('[PostHog] Tracking event:', eventName);
            await Posthog.capture(event);
        } catch (error) {
            console.debug('[PostHog] Failed to track event, queueing for later:', error);
            this.queueEvent(event);
        }
    }

    // Track screen views
    async trackScreen(screenName, properties = {}) {
        if (!this.initialized) {
            console.debug('[PostHog] Analytics not initialized, queueing screen:', screenName);
            this.queueEvent({
                event: 'screen_view',
                properties: {
                    screen_name: screenName,
                    ...properties,
                    platform: Capacitor.getPlatform(),
                    timestamp: new Date().toISOString()
                }
            });
            return;
        }

        try {
            console.log('[PostHog] Tracking screen:', screenName);
            await Posthog.screen({
                screenTitle: screenName,
                properties: {
                    ...properties,
                    platform: Capacitor.getPlatform(),
                    timestamp: new Date().toISOString()
                }
            });
        } catch (error) {
            console.debug('[PostHog] Failed to track screen:', error);
        }
    }

    // Set user properties
    async setUserProperties(properties) {
        if (!this.initialized) {
            console.debug('[PostHog] Cannot set properties: analytics not initialized');
            return;
        }

        try {
            // PostHog Capacitor plugin handles this via identify
            console.debug('[PostHog] Setting user properties (via next identify call)');
        } catch (error) {
            console.debug('[PostHog] Failed to set properties:', error);
        }
    }

    // Reset analytics (e.g., on logout)
    async reset() {
        if (!this.initialized) {
            console.debug('[PostHog] Cannot reset: analytics not initialized');
            return;
        }

        try {
            console.log('[PostHog] Resetting user');
            await Posthog.reset();
            // Clear the event queue when user is reset
            eventQueue = [];
        } catch (error) {
            console.debug('[PostHog] Failed to reset:', error);
        }
    }

    // Track timing of actions
    startTimer(eventName) {
        this.timers = this.timers || {};
        this.timers[eventName] = Date.now();
    }

    async endTimer(eventName, properties = {}) {
        if (!this.timers || !this.timers[eventName]) return;
        const duration = Date.now() - this.timers[eventName];
        delete this.timers[eventName];
        await this.track(eventName, {
            ...properties,
            duration_ms: duration
        });
    }

    // Track feature usage
    async trackFeature(featureName, properties = {}) {
        await this.track(`feature_used_${featureName}`, {
            platform: Capacitor.getPlatform(),
            ...properties
        });
    }

    // Track errors
    async trackError(error, context = {}) {
        await this.track('error_occurred', {
            error_name: error.name,
            error_message: error.message,
            error_stack: error.stack,
            platform: Capacitor.getPlatform(),
            ...context
        });
    }
}

// Export singleton instance
export const analytics = new AnalyticsService();

// Export convenience methods
export const trackEvent = (eventName, properties) => analytics.track(eventName, properties);
export const trackScreen = (screenName, properties) => analytics.trackScreen(screenName, properties);
export const identifyUser = (userId, traits) => analytics.identify(userId, traits);
export const setUserProperties = (properties) => analytics.setUserProperties(properties);
export const resetAnalytics = () => analytics.reset();
export const trackFeature = (featureName, properties) => analytics.trackFeature(featureName, properties);
export const trackError = (error, context) => analytics.trackError(error, context);